import { useState } from 'react'
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { 
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  TagIcon,
  CubeIcon,
  CogIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { GET_PRODUCT, GET_BUNDLE_GROUPS, GET_PRODUCTS } from '../graphql/product-queries'
import { Product } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
import EditMealPartModal from '../components/EditMealPartModal'
import AllergenAttributeEditor from '../components/AllergenAttributeEditor'
import toast from 'react-hot-toast'

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'basic' | 'variants' | 'meal-config'>('basic')
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false)
  const [showEditGroupModal, setShowEditGroupModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState<any>(null)

  const { data: productData, loading: productLoading, error: productError } = useQuery(GET_PRODUCT, {
    variables: { id: parseInt(id!) },
    skip: !id
  })

  const { data: bundleData, loading: bundleLoading, refetch: refetchBundleGroups } = useQuery(GET_BUNDLE_GROUPS, {
    variables: { bundleProductId: parseInt(id!) },
    skip: !id || productData?.product?.kind !== 'BUNDLE'
  })

  const { data: productsData, loading: productsLoading } = useQuery(GET_PRODUCTS, {
    variables: { filters: { active: true } },
    skip: !showEditGroupModal
  })

  const product: Product = productData?.product
  const bundleGroups = bundleData?.bundleGroups || []
  const allProducts = productsData?.products || []

  if (productLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (productError || !product) {
    return <Navigate to="/products" replace />
  }

  const getProductKindColor = (kind: string) => {
    switch (kind) {
      case 'BASE': return 'bg-blue-100 text-blue-800'
      case 'MODIFIER': return 'bg-green-100 text-green-800'
      case 'BUNDLE': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getProductKindIcon = (kind: string) => {
    switch (kind) {
      case 'BASE': return '🍽️'
      case 'MODIFIER': return '➕'
      case 'BUNDLE': return '📦'
      default: return '📋'
    }
  }

  const formatPrice = (priceCents: number) => {
    return `€${(priceCents / 100).toFixed(2)}`
  }

  const handleVariantClick = (variantId: number) => {
    navigate(`/products/${product.id}/variants/${variantId}`)
  }

  const handleEditGroup = (group: any) => {
    setEditingGroup(group)
    setShowEditGroupModal(true)
  }

  const handleCloseEditModal = () => {
    setShowEditGroupModal(false)
    setEditingGroup(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/products"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{product.name}</h1>
            <p className="text-sm text-gray-500">Product Details</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Link
            to={`/products/${product.id}/edit`}
            className="btn-secondary"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </Link>
          <button
            onClick={() => {
              // In a real implementation, you'd call the delete mutation here
              toast.success('Delete functionality would be implemented here')
            }}
            className="btn-danger"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      {product.kind === 'BUNDLE' && (
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'basic', name: 'Basic Info', icon: CubeIcon },
              { id: 'variants', name: 'Variants', icon: TagIcon },
              { id: 'meal-config', name: 'Meal Configuration', icon: CogIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab Content */}
      {product.kind === 'BUNDLE' ? (
        <>
          {activeTab === 'basic' && (
            <div className="space-y-6">
              {/* Basic Info Content */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Info */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Basic Info */}
                  <div className="card">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Name</label>
                        <p className="mt-1 text-sm text-gray-900">{product.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Type</label>
                        <div className="mt-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getProductKindColor(product.kind)}`}>
                            {getProductKindIcon(product.kind)} {product.kind}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Status</label>
                        <div className="mt-1 flex gap-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            product.active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {product.active ? 'Active' : 'Inactive'}
                          </span>
                          {product.display_in_pos && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              POS
                            </span>
                          )}
                          {product.display_in_webshop && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                              Webshop
                            </span>
                          )}
                          {(product.platforms ?? [])
                            .filter(a => a.enabled)
                            .sort((a, b) => a.platform.sortOrder - b.platform.sortOrder)
                            .map(a => (
                              <span
                                key={a.platform.slug}
                                className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800"
                              >
                                {a.platform.displayName}
                              </span>
                            ))}
                        </div>
                      </div>
                      {product.description && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-500">Description</label>
                          <p className="mt-1 text-sm text-gray-900">{product.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Quick Stats */}
                  <div className="card">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Variants</span>
                        <span className="text-sm font-medium text-gray-900">{product.variants?.length || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Categories</span>
                        <span className="text-sm font-medium text-gray-900">{product.categories.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Last Updated</span>
                        <span className="text-sm font-medium text-gray-900">
                          {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'variants' && (
            <div className="space-y-6">
              {/* Variants Content */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Variants */}
                  <div className="card">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-medium text-gray-900">Variants</h2>
                      <Link
                        to={`/products/${product.id}/variants/new`}
                        className="btn-primary text-sm"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Variant
                      </Link>
                    </div>
                    
                    {product.variants && product.variants.length > 0 ? (
                      <div className="space-y-4">
                        {product.variants.map((variant) => (
                          <div 
                            key={variant.id} 
                            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => handleVariantClick(variant.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    <CubeIcon className="h-5 w-5 text-gray-400" />
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="text-sm font-medium text-gray-900">{variant.name}</h3>
                                    {variant.sku && (
                                      <p className="text-sm text-gray-500">SKU: {variant.sku}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-4">
                                <div className="text-right">
                                  <div className="text-sm font-medium text-gray-900">
                                    {formatPrice(variant.price_cents)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {(variant.deposit_cents ?? 0) > 0
                                      ? `Price · +${formatPrice(variant.deposit_cents ?? 0)} statiegeld`
                                      : 'Price'}
                                  </div>
                                </div>
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  variant.active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {variant.active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No variants</h3>
                        <p className="mt-1 text-sm text-gray-500">Get started by creating your first variant.</p>
                        <div className="mt-6">
                          <Link
                            to={`/products/${product.id}/variants/new`}
                            className="btn-primary"
                          >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add Variant
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Quick Stats */}
                  <div className="card">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Total Variants</span>
                        <span className="text-sm font-medium text-gray-900">{product.variants?.length || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Active Variants</span>
                        <span className="text-sm font-medium text-gray-900">
                          {product.variants?.filter(v => v.active).length || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Price Range</span>
                        <span className="text-sm font-medium text-gray-900">
                          {product.variants && product.variants.length > 0 
                            ? `€${(Math.min(...product.variants.map(v => v.price_cents)) / 100).toFixed(2)} - €${(Math.max(...product.variants.map(v => v.price_cents)) / 100).toFixed(2)}`
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'meal-config' && (
            <div className="space-y-6">
              {/* Meal Configuration Content */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Meal Groups */}
                  <div className="card">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-medium text-gray-900">Meal Parts</h2>
                      <button
                        onClick={() => setShowCreateGroupModal(true)}
                        className="btn-primary text-sm"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Meal Part
                      </button>
                    </div>
                    
                    {bundleLoading ? (
                      <LoadingSpinner />
                    ) : bundleGroups.length > 0 ? (
                      <div className="space-y-4">
                        {bundleGroups.map((group: any) => (
                          <div key={group.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <CogIcon className="h-5 w-5 text-gray-400" />
                                <div>
                                  <h3 className="text-lg font-medium text-gray-900">{group.name}</h3>
                                  <p className="text-sm text-gray-500">
                                    Required: {group.required_count} item{group.required_count !== 1 ? 's' : ''}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button 
                                  onClick={() => handleEditGroup(group)}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                  title="Edit meal part"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button className="p-1 text-red-400 hover:text-red-600">
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Allowed Items - Grouped by Product */}
                            <div className="mt-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">Allowed Items</h4>
                              {group.options && group.options.length > 0 ? (
                                <div className="space-y-4">
                                  {(() => {
                                    // Group options by product name
                                    const groupedByProduct = group.options.reduce((acc: any, option: any) => {
                                      const productName = option.eligible_variant.product?.name || 'Unknown Product'
                                      if (!acc[productName]) {
                                        acc[productName] = []
                                      }
                                      acc[productName].push(option)
                                      return acc
                                    }, {})

                                    return Object.entries(groupedByProduct).map(([productName, options]: [string, any]) => (
                                      <div key={productName} className="border border-gray-200 rounded-lg p-3">
                                        <div className="flex items-center justify-between mb-2">
                                          <h5 className="text-sm font-medium text-gray-800">{productName}</h5>
                                          <span className="text-xs text-gray-500">
                                            {options.length} variant{options.length !== 1 ? 's' : ''}
                                          </span>
                                        </div>
                                        <div className="space-y-1">
                                          {options.map((option: any) => (
                                            <div key={option.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                              <div className="flex items-center space-x-3">
                                                <div className="flex-shrink-0">
                                                  <CubeIcon className="h-4 w-4 text-gray-400" />
                                                </div>
                                                <div>
                                                  <p className="text-sm font-medium text-gray-900">
                                                    {option.eligible_variant.name}
                                                  </p>
                              <div className="flex items-center space-x-2 text-xs text-gray-500">
                                                    <span>Base: €{(option.eligible_variant.price_cents / 100).toFixed(2)}</span>
                                                    {option.eligible_variant.sku && (
                                                      <span>SKU: {option.eligible_variant.sku}</span>
                                                    )}
                                                    <span className={`px-1 py-0.5 rounded text-xs ${
                                                      option.eligible_variant.active 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-red-100 text-red-800'
                                                    }`}>
                                                      {option.eligible_variant.active ? 'Active' : 'Inactive'}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="flex items-center space-x-2">
                                                <span className={`text-sm font-medium ${
                                                  option.price_delta_cents > 0 ? 'text-green-600' : 
                                                  option.price_delta_cents < 0 ? 'text-red-600' : 'text-gray-900'
                                                }`}>
                                                  {option.price_delta_cents >= 0 ? '+' : ''}€{(option.price_delta_cents / 100).toFixed(2)}
                                                </span>
                                                <button 
                                                  className="p-1 text-gray-400 hover:text-gray-600"
                                                  title="Edit option"
                                                >
                                                  <PencilIcon className="h-3 w-3" />
                                                </button>
                                                <button 
                                                  className="p-1 text-red-400 hover:text-red-600"
                                                  title="Remove option"
                                                >
                                                  <TrashIcon className="h-3 w-3" />
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))
                                  })()}
                                </div>
                              ) : (
                                <div className="text-center py-4">
                                  <p className="text-sm text-gray-500">No items added yet</p>
                                  <button className="mt-2 text-sm text-primary-600 hover:text-primary-900">
                                    + Add Item
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <CogIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No meal parts configured</h3>
                        <p className="mt-1 text-sm text-gray-500">Define the parts that make up this meal deal.</p>
                        <div className="mt-6">
                          <button
                            onClick={() => setShowCreateGroupModal(true)}
                            className="btn-primary"
                          >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add Meal Part
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Quick Stats */}
                  <div className="card">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Meal Configuration</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Meal Parts</span>
                        <span className="text-sm font-medium text-gray-900">{bundleGroups.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Total Options</span>
                        <span className="text-sm font-medium text-gray-900">
                          {bundleGroups.reduce((sum: number, group: any) => sum + (group.options?.length || 0), 0)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Unique Products</span>
                        <span className="text-sm font-medium text-gray-900">
                          {(() => {
                            const allProducts = new Set()
                            bundleGroups.forEach((group: any) => {
                              group.options?.forEach((option: any) => {
                                if (option.eligible_variant.product?.name) {
                                  allProducts.add(option.eligible_variant.product.name)
                                }
                              })
                            })
                            return allProducts.size
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Required Selections</span>
                        <span className="text-sm font-medium text-gray-900">
                          {bundleGroups.reduce((sum: number, group: any) => sum + group.required_count, 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="card">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">How it works</h3>
                    <div className="text-sm text-gray-600 space-y-2">
                      <p><strong>Meal Parts:</strong> Define the slots (Main, Drink, Side)</p>
                      <p><strong>Required:</strong> How many items must be selected</p>
                      <p><strong>Products:</strong> Options are grouped by product for easy management</p>
                      <p><strong>Variants:</strong> Each product can have multiple variants</p>
                      <p><strong>Price Delta:</strong> Extra cost for premium options</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Non-bundle product content */
        <div className="space-y-6">
          {/* Product Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className="card">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Name</label>
                    <p className="mt-1 text-sm text-gray-900">{product.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Type</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getProductKindColor(product.kind)}`}>
                        {getProductKindIcon(product.kind)} {product.kind}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Status</label>
                    <div className="mt-1 flex gap-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        product.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {product.active ? 'Active' : 'Inactive'}
                      </span>
                      {product.display_in_pos && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          POS
                        </span>
                      )}
                      {product.display_in_webshop && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          Webshop
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Created</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {product.created_at ? new Date(product.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                {product.description && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-500">Description</label>
                    <p className="mt-1 text-sm text-gray-900">{product.description}</p>
                  </div>
                )}
              </div>

              {/* Allergens & Attributes */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Allergens & Attributes</h2>
                  <Link to={`/products/${product.id}/edit`} className="text-sm text-primary-600 hover:text-primary-900">
                    Edit
                  </Link>
                </div>
                <AllergenAttributeEditor
                  readOnly
                  value={{
                    allergens: product.allergen_info_json?.allergens ?? [],
                    attributes: product.attributes_json?.attributes ?? [],
                  }}
                />
              </div>

              {/* Variants */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Variants</h2>
                  <Link
                    to={`/products/${product.id}/variants/new`}
                    className="btn-primary text-sm"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Variant
                  </Link>
                </div>
                {product.variants && product.variants.length > 0 ? (
                  <div className="space-y-4">
                    {product.variants.map((variant) => (
                      <div 
                        key={variant.id} 
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleVariantClick(variant.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <CubeIcon className="h-5 w-5 text-gray-400" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-medium text-gray-900">{variant.name}</h3>
                                {variant.sku && (
                                  <p className="text-sm text-gray-500">SKU: {variant.sku}</p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {formatPrice(variant.price_cents)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {(variant.deposit_cents ?? 0) > 0
                                  ? `Price · +${formatPrice(variant.deposit_cents ?? 0)} statiegeld`
                                  : 'Price'}
                              </div>
                            </div>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              variant.active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {variant.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No variants</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by creating your first variant.</p>
                    <div className="mt-6">
                      <Link
                        to={`/products/${product.id}/variants/new`}
                        className="btn-primary"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add Variant
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Variants</span>
                    <span className="text-sm font-medium text-gray-900">{product.variants?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Categories</span>
                    <span className="text-sm font-medium text-gray-900">{product.categories.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Last Updated</span>
                    <span className="text-sm font-medium text-gray-900">
                      {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Bundle Group Modal */}
      {showCreateGroupModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add Meal Part</h3>
                <button
                  onClick={() => setShowCreateGroupModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Part Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Main Course, Drink, Side"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Required Selections
                  </label>
                  <input
                    type="number"
                    min="1"
                    defaultValue="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    How many items must be selected from this part
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateGroupModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
                  >
                    Create Part
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Bundle Group Modal */}
      {showEditGroupModal && editingGroup && (
        <EditMealPartModal
          group={editingGroup}
          allProducts={allProducts}
          productsLoading={productsLoading}
          onClose={handleCloseEditModal}
          onSave={() => {
            // Refetch bundle groups to show updated data
            refetchBundleGroups()
            handleCloseEditModal()
          }}
        />
      )}
    </div>
  )
}

export default ProductDetailPage